import React from 'react';
import { FaSync } from 'react-icons/fa';

import { Tooltip } from '@getethos/ethos-design-system-v2';
import throttle from 'lodash.throttle';

import { FlexCol, FlexRow, TitleSmall } from '../../../common/components/v2';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { getFormattedTimestamp } from '../../../common/helpers';
import { ITask } from '../../tasks/interfaces/ITask';
import { CarrierEligibility } from '../../tasks/types/carrierEligibility';
import styles from '../styles.module.scss';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskNote } from './components/TaskNote';

// copied from underwriting/maestro/src/evidence/dto/eligibility.dto.ts
interface PolicyInfoDto {
  policyNumber: string;
  status: string;
  statusDate: Date;
  issueDate: Date;
  insuredName: string;
  faceAmount: number;
  productDesc: string;
  dob: Date;
  finalClass: string;
  finalTableRating: number;
}

interface EligibilityStatusDto {
  message: string;
  errorCode: number;
  errors: [];
  success: boolean;
}

interface EligibilityDto {
  totalFaceAmount?: number;
  isPendingPolicyExist?: boolean;
  hasUserDetails?: boolean;
  message?: string;
  existingPendingPolicies: PolicyInfoDto[];
  approvedAndDeclinedPolicies: PolicyInfoDto[];
  status: EligibilityStatusDto;
  hasError?: boolean;
  validationErrorMessage?: string;
}

function formatPolicy(policy: PolicyInfoDto): string {
  let result = '';
  if (policy.policyNumber) {
    result += `  Policy Number: ${policy.policyNumber}\n`;
    if (policy.status) {
      result += `    status: ${policy.status}\n`;
    }
    if (policy.statusDate) {
      result += `    status date: ${policy.statusDate}\n`;
    }
    if (policy.issueDate) {
      result += `    issue date: ${policy.issueDate}\n`;
    }
    if (policy.insuredName) {
      result += `    insured name: ${policy.insuredName}\n`;
    }
    if (policy.faceAmount) {
      result += `    face amount: ${policy.faceAmount}\n`;
    }
    if (policy.productDesc) {
      result += `    product description: ${policy.productDesc}\n`;
    }
    if (policy.dob) {
      result += `    dob: ${policy.dob}\n`;
    }
    if (policy.finalClass) {
      result += `    final class: ${policy.finalClass}\n`;
    }
    if (policy.finalTableRating || policy.finalTableRating === 0) {
      result += `    final table rating: ${policy.finalTableRating}\n`;
    }
  }
  return result;
}

function formatCarrierEligibility(note: object | undefined): string {
  if (note) {
    const data = note as EligibilityDto;
    let result = '';
    if (data.hasError) {
      result += `Error: ${data.validationErrorMessage}\n`;
    }
    if (data.status) {
      const status = data.status;
      if (status.success) {
        result += 'Status: success\n';
      } else {
        result += `Status: error\n`;
        if (status.errorCode) {
          result += `  code: ${status.errorCode}\n`;
        }
        if (status.message) {
          result += `  message: ${status.message}\n`;
        }
      }
    }
    if (data.message) {
      result += `Message: ${data.message}\n`;
    }
    // make sure we show 0 face amount (0 eval to false in js)
    if (data.totalFaceAmount || data.totalFaceAmount === 0) {
      result += `Total Face Amount: ${data.totalFaceAmount}\n`;
    }
    if (
      data.existingPendingPolicies &&
      Array.isArray(data.existingPendingPolicies) &&
      data.existingPendingPolicies.length > 0
    ) {
      result += `Existing/Pending policies:\n`;
      data.existingPendingPolicies.forEach(policy => {
        result += formatPolicy(policy);
      });
    }
    if (
      data.approvedAndDeclinedPolicies &&
      Array.isArray(data.approvedAndDeclinedPolicies) &&
      data.approvedAndDeclinedPolicies.length > 0
    ) {
      result += `Approved/Declined policies:\n`;
      data.approvedAndDeclinedPolicies.forEach(policy => {
        result += formatPolicy(policy);
      });
    }
    if (result === '') {
      result = 'no valid data to show';
    }
    return result;
  } else {
    return 'error to retrieve data';
  }
}

export const CarrierEligibilityDetail = ({
  task,
  taskStateHandler,
  retryHandler,
}: {
  task: ITask;
  retryHandler: (taskId: number) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const taskDetail = task.taskData as CarrierEligibility;
  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  const onRetry = throttle(
    () => {
      if (retryHandler) {
        retryHandler(task.id);
      }
    },
    5000,
    { leading: true, trailing: false },
  );

  return (
    <div data-label={ElementLocators.LOCATOR_RULE_REVIEW_SECTION}>
      <FlexRow id={task.id.toString()} className={styles.Title}>
        <FlexCol className={styles.TitleTaskIcon}>
          <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
        </FlexCol>
        <TitleSmall>{Resource.TASK_TITLE_CARRIER_ELIGIBILITY}</TitleSmall>
        {![TaskState.COMPLETED, TaskState.FOR_REVIEW].includes(task.state) && (
          <FlexCol className={styles.CreatedDate}>
            {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
            <Tooltip
              content={Resource.REPULL_CARRIER_ELIGIBILITY}
              placement="bottom"
            >
              <div>
                <button className={styles.RepullButton} onClick={onRetry}>
                  <FaSync />
                </button>
              </div>
            </Tooltip>
          </FlexCol>
        )}
      </FlexRow>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}

      {task.state !== TaskState.REQUESTED && (
        <TaskNote
          taskID={task.id}
          originalNote={formatCarrierEligibility(taskDetail?.note)}
        />
      )}
    </div>
  );
};
